import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';

export function useSchoolFeatures() {
  const { school } = useSchoolQuery();

  // Normalise once — the stored school_type has historically arrived in mixed
  // case (e.g. "Tertiary" from the Filament admin panel), so compare lower-case.
  const schoolType = computed(() => school.value?.school_type?.toLowerCase() ?? null);

  const isTertiary = computed(() => schoolType.value === 'tertiary');

  const isSecondary = computed(() => schoolType.value === 'secondary');

  const isPrimary = computed(() => schoolType.value === 'primary');

  const settings = computed(() => (school.value as { settings?: Record<string, unknown> } | null)?.settings ?? {});

  const allowStudentPhotoUpload = computed(
    () => !!settings.value?.allow_student_photo_upload,
  );

  const features = computed(() => ({
    hasCampuses: isTertiary.value,
    hasFaculties: isTertiary.value,
    hasDepartments: isTertiary.value,
    hasProgrammes: isTertiary.value,
    hasCourses: isTertiary.value,
    hasClasses: isSecondary.value || isPrimary.value, // Traditional classes
    allowStudentPhotoUpload: allowStudentPhotoUpload.value,
  }));

  const isParentSchool = computed(() => {
    const s = school.value as { is_parent?: boolean; parent_features_enabled?: boolean } | null;
    return !!s?.is_parent || !!s?.parent_features_enabled;
  });

  const isChildSchool = computed(() => !!(school.value as { is_child?: boolean } | null)?.is_child);

  const isStandaloneSchool = computed(() => !!(school.value as { is_standalone?: boolean } | null)?.is_standalone);

  return {
    schoolType,
    isTertiary,
    isSecondary,
    isPrimary,
    isParentSchool,
    isChildSchool,
    isStandaloneSchool,
    features,
    allowStudentPhotoUpload,
  };
}
